import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../utils/config';
import { NewRequestModalProps, NewRequestFormData } from '../types';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import '../styles/NewRequestModal.css';
// import ProductSearchModal from './ProductSearchModal';
import ProductSelectionModal from './ProductSelectionModal';
import DatePicker from './DatePicker';
import AddressModal from './AddressModal';
import ApprovedSuppliersModal from './ApprovedSuppliersModal';


// Region options - will be populated dynamically
let REGION_OPTIONS: { value: string; label: string }[] = [];

// Category options - will be populated dynamically
let CATEGORY_OPTIONS: { value: string; label: string }[] = [];

// Currency options - will be populated dynamically
let CURRENCY_OPTIONS: { value: string; label: string }[] = [];

// Unit options - will be populated dynamically
let UNIT_OPTIONS: { value: string; label: string }[] = [];

// Predefined filter suggestions - will be populated dynamically
let FILTER_SUGGESTIONS: any[] = [
  {
    key: 'certificate',
    label: 'Certificate',
    type: 'text',
    placeholder: 'Enter certification requirements'
  },
  {
    key: 'color',
    label: 'Color',
    type: 'dropdown',
    options: ['Red', 'Blue', 'Green', 'Yellow', 'Black', 'White', 'Gray', 'Orange', 'Purple', 'Pink', 'Brown', 'Custom']
  },
  {
    key: 'size',
    label: 'Size',
    type: 'dropdown',
    options: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'Custom Size', 'Standard Size']
  },
  {
    key: 'standard_norm',
    label: 'Standard/Norm',
    type: 'dropdown',
    options: ['ISO 9001', 'ISO 14001', 'CE Marking', 'FDA Approved', 'RoHS Compliant', 'REACH Compliant', 'UL Listed', 'Custom Standard']
  }
];

// Info tooltip component
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <div className="info-tooltip">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
      <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
    <div className="tooltip-content">
      <div className="tooltip-text">{text}</div>
    </div>
  </div>
);

const NewRequestModal: React.FC<NewRequestModalProps> = ({ isOpen, onClose, onSubmit, editingRequest, isSubmitting = false }) => {
  const { t, currentLanguage } = useTranslation();
  const { user } = useAuth();
  
  // Get dynamic company product code label
  const getCompanyProductCodeLabel = () => {
    const companyName = user?.company?.name || 'Company';
    return `${companyName} Product Code`;
  };
  
  // Get default values based on current language
  const getDefaultValues = () => {
    if (currentLanguage === 'tr') {
      return {
        currency: 'TRY',
        region: ['Türkiye, İstanbul']
      };
    } else if (currentLanguage === 'de') {
      return {
        currency: 'EUR',
        region: ['DE']
      };
    } else {
      return {
        currency: 'CHF',
        region: ['CH']
      };
    }
  };
  
  // Initialize options dynamically based on current language
  React.useEffect(() => {
    REGION_OPTIONS = [
      { value: 'CH', label: t('common.switzerland_ch') },
      { value: 'DE', label: t('common.germany_de') },
      { value: 'Türkiye, İstanbul', label: 'Türkiye, İstanbul' },
      { value: 'DACH', label: t('common.dach_region') },
      { value: 'EU', label: t('common.european_union_eu') },
      { value: 'US', label: t('common.united_states_us') },
      { value: 'ASIA', label: t('common.asia_pacific') },
      { value: 'GLOBAL', label: t('common.global') }
    ];

    CATEGORY_OPTIONS = [
      { value: 'IT', label: t('common.information_technology_it') },
      { value: 'HR', label: t('common.human_resources_hr') },
      { value: 'FINANCE', label: t('common.finance_accounting') },
      { value: 'MARKETING', label: t('common.marketing_sales') },
      { value: 'OPERATIONS', label: t('common.operations') },
      { value: 'R&D', label: t('common.research_development') },
      { value: 'LEGAL', label: t('common.legal_compliance') },
      { value: 'FACILITIES', label: t('common.facilities_maintenance') },
      { value: 'PROCUREMENT', label: t('common.procurement') },
      { value: 'QUALITY', label: t('common.quality_assurance') },
      { value: 'SUPPLY_CHAIN', label: t('common.supply_chain') },
      { value: 'CUSTOMER_SERVICE', label: t('common.customer_service') },
      { value: 'OTHER', label: t('common.other') }
    ];

    CURRENCY_OPTIONS = [
      { value: 'CHF', label: t('common.swiss_franc_chf') },
      { value: 'EUR', label: t('common.euro_eur') },
      { value: 'USD', label: t('common.us_dollar_usd') },
      { value: 'GBP', label: t('common.british_pound_gbp') },
      { value: 'TRY', label: t('common.turkish_lira_try') }
    ];

    UNIT_OPTIONS = [
      { value: 'piece', label: t('common.piece') },
      { value: 'kg', label: t('common.kilogram') },
      { value: 'm', label: t('common.meter') },
      { value: 'liter', label: t('common.liter') },
      { value: 'box', label: t('common.box') },
      { value: 'set', label: t('common.set') },
      { value: 'hour', label: t('common.hour') },
      { value: 'day', label: t('common.day') }
    ];

    // Update FILTER_SUGGESTIONS with translated labels
    FILTER_SUGGESTIONS = [
      {
        key: 'certificate',
        label: t('common.certificate'),
        type: 'text',
        placeholder: t('common.enter_certification_requirements')
      },
      {
        key: 'color',
        label: t('common.color'),
        type: 'dropdown',
        options: [t('common.red'), t('common.blue'), t('common.green'), t('common.yellow'), t('common.black'), t('common.white'), t('common.gray'), t('common.orange'), t('common.purple'), t('common.pink'), t('common.brown'), t('common.custom')]
      },
      {
        key: 'size',
        label: t('common.size'),
        type: 'dropdown',
        options: [t('common.xs'), t('common.s'), t('common.m'), t('common.l'), t('common.xl'), t('common.xxl'), t('common.custom_size'), t('common.standard_size')]
      },
      {
        key: 'standard_norm',
        label: t('common.standard_norm'),
        type: 'dropdown',
        options: [t('common.iso_9001'), t('common.iso_14001'), t('common.ce_marking'), t('common.fda_approved'), t('common.rohs_compliant'), t('common.reach_compliant'), t('common.ul_listed'), t('common.custom_standard')]
      }
    ];
  }, [t]);
  
  // Update form data when language changes
  React.useEffect(() => {
    if (isOpen && !editingRequest) {
      const defaults = getDefaultValues();
      setFormData(prev => ({
        ...prev,
        currency: defaults.currency,
        region: defaults.region
      }));
    }
  }, [currentLanguage, isOpen, editingRequest]);
  
  const [formData, setFormData] = useState<NewRequestFormData>(() => {
    const defaults = getDefaultValues();
    return {
      // Product Information
      product_name: '',
      product_brand: '',
      product_global_code: '',
      product_company_code: '', // Legacy field
      company_product_code: '', // New dynamic field
      product_description: '',
      
      // Product Specifications
      dimensions_text: '',
      
      base_unit: {
        unit: 'adet',
        conversion_factor: 1.0
      },
      
      // Request Information
      quantity: '',
      unit: 'piece',
      budget: '',
      currency: defaults.currency,
      delivery_time: '',
      importance: 'price',
      region: defaults.region,
      category: 'IT', // Default category
      address: '', // Delivery address
    
    // Supplier Information
    current_supplier: '',
    favorite_suppliers: [],
    
    // Additional Specifications
    additional_specifications: [],
    custom_specifications: [],
    
    // OCR Price Information
    unit_price: 0,
    total_price: 0,
    
    // Legacy fields
    item: '',
    price: 0,
    firmName: '',
    urgency: 'normal',
    currentPrice: 0,
    additionalNotes: '',
    customFilters: [],
    file: undefined
    };
  });

  // Effect to populate form when editing a request (for revisions)
  useEffect(() => {
    
    if (editingRequest && isOpen) {
      
      // Convert backend data to form data
      const backendData = editingRequest as any; // Type assertion for backend data
      
      // Handle custom_specifications format conversion
      let customSpecs = [];
      if (backendData.custom_specifications) {
        if (Array.isArray(backendData.custom_specifications)) {
          customSpecs = backendData.custom_specifications;
        } else if (typeof backendData.custom_specifications === 'object') {
          customSpecs = Object.entries(backendData.custom_specifications).map(([key, value]) => ({
            key: key,
            value: value as string
          }));
        }
      }
      
      console.log('🔍 DEBUG: Processed custom specifications:', customSpecs);
      
      // Map backend data to form data exactly like RequestDetailModal shows
      const newFormData: NewRequestFormData = {
        // Product Information - map from editingRequest fields
        product_name: backendData.product_name || backendData.item || '',
        product_brand: backendData.product_brand || '',
        product_global_code: backendData.product_global_code || '',
        product_company_code: backendData.product_company_code || '',
        product_description: backendData.product_description || '',
        
        // Request Information - map from editingRequest fields
        quantity: backendData.quantity || 1,
        unit: backendData.unit || 'piece',
        budget: backendData.budget || backendData.price || 0,
        currency: backendData.currency || getDefaultValues().currency,
        delivery_time: backendData.delivery_time || '',
        importance: backendData.importance || 'price',
        region: Array.isArray(backendData.region) ? backendData.region : [backendData.region || getDefaultValues().region[0]],
        category: backendData.category || backendData.product_category || 'IT',
        address: backendData.address || '',
        
        // Supplier Information - map from editingRequest fields
        current_supplier: backendData.current_supplier || '',
        favorite_suppliers: Array.isArray(backendData.favorite_suppliers) ? backendData.favorite_suppliers : [],
        
        // Additional Specifications - map from editingRequest fields
        additional_specifications: Array.isArray(backendData.additional_specifications) ? backendData.additional_specifications : [],
        custom_specifications: customSpecs,
        
        // OCR Price Information
        unit_price: backendData.unit_price || 0,
        total_price: backendData.total_price || 0,
        
        // Legacy fields for backend compatibility
        item: backendData.product_name || backendData.item || '',
        price: backendData.budget || backendData.price || 0,
        firmName: backendData.current_supplier || '',
        urgency: backendData.urgency || 'normal',
        currentPrice: backendData.current_price || 0,
        additionalNotes: backendData.additional_notes || '',
        customFilters: backendData.custom_filters ? Object.entries(backendData.custom_filters).map(([key, value]) => ({
          key: key,
          value: value as string
        })) : []
      };
      
      console.log('🔍 NewRequestModal - New form data:', newFormData);
      console.log('🔍 NewRequestModal - Product name:', newFormData.product_name);
      console.log('🔍 NewRequestModal - Product brand:', newFormData.product_brand);
      console.log('🔍 NewRequestModal - Quantity:', newFormData.quantity);
      console.log('🔍 NewRequestModal - Budget:', newFormData.budget);
      console.log('🔍 NewRequestModal - Category:', newFormData.category);
      console.log('🔍 NewRequestModal - Unit:', newFormData.unit);
      console.log('🔍 NewRequestModal - Currency:', newFormData.currency);
      console.log('🔍 NewRequestModal - Additional Specifications:', newFormData.additional_specifications);
      console.log('🔍 NewRequestModal - Custom Specifications:', newFormData.custom_specifications);
      setFormData(newFormData);
      console.log('🔍 NewRequestModal - Form data set successfully');
    } else if (!editingRequest && isOpen) {
      // Reset form when creating new request
      console.log('🔍 NewRequestModal - Resetting form for new request');
      setFormData({
        // Product Information
        product_name: '',
        product_brand: '',
        product_global_code: '',
        product_company_code: '',
        product_description: '',
        
        // Request Information
        quantity: 1,
        unit: 'piece',
        budget: 0,
        currency: getDefaultValues().currency,
        delivery_time: '',
        importance: 'price',
        region: getDefaultValues().region,
        category: 'IT',
        address: '',
        
        // Supplier Information
        current_supplier: '',
        favorite_suppliers: [],
        selected_approved_suppliers: [],
        
        // Additional Specifications
        additional_specifications: [],
        custom_specifications: [],
        
        // OCR Price Information
        unit_price: 0,
        total_price: 0,
        
        // Legacy fields
        item: '',
        price: 0,
        firmName: '',
        urgency: 'normal',
        currentPrice: 0,
        additionalNotes: '',
        customFilters: [],
        file: undefined
      });
    }
  }, [editingRequest, isOpen]);


  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newCustomSpecKey, setNewCustomSpecKey] = useState('');
  const [newCustomSpecValue, setNewCustomSpecValue] = useState('');
  const [newFavoriteSupplier, setNewFavoriteSupplier] = useState('');
  const [activeFilterType, setActiveFilterType] = useState<string | null>(null);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [ocrProducts, setOcrProducts] = useState<any[]>([]);
  const [isApprovedSuppliersModalOpen, setIsApprovedSuppliersModalOpen] = useState(false);
  const [ocrDocumentInfo, setOcrDocumentInfo] = useState<any>({});
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const { token } = useAuth();

  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'product_name':
        return !value || value.trim() === '' ? t('components.new_request_modal.product_name_required') : '';
      case 'quantity':
        return !value || value === '' || value === 0 ? t('components.new_request_modal.quantity_required') : '';
      case 'budget':
        return !value || value === '' || value === 0 ? t('components.new_request_modal.budget_required') : '';
      default:
        return '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    console.log('🔍 Input change - name:', name, 'value:', value);
    
    // Clear validation error for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
    
    // Handle nested fields (base_unit)
    if (name.includes('.')) {
      const [parentKey, childKey] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parentKey]: {
          ...(prev[parentKey as keyof NewRequestFormData] as any || {}),
          [childKey]: parentKey === 'base_unit' ? (childKey === 'conversion_factor' ? Number(value) : value) : value
        }
      }));
      return;
    }
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: ['quantity', 'budget', 'currentPrice', 'unit_price', 'total_price'].includes(name) ? Number(value) : value
      };
      console.log('🔍 Updated formData:', newData);
      return newData;
    });
  };

  const handleAddressSelect = (selectedAddress: string) => {
    console.log('🔍 Address selected:', selectedAddress);
    setFormData(prev => ({
      ...prev,
      address: selectedAddress
    }));
  };

  const handleApprovedSuppliersSelect = (selectedSuppliers: string[]) => {
    console.log('🔍 Approved suppliers selected:', selectedSuppliers);
    setFormData(prev => {
      const newData = {
        ...prev,
        selected_approved_suppliers: selectedSuppliers
      };
      console.log('🔍 Updated formData with approved suppliers:', newData);
      return newData;
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData(prev => ({ ...prev, file }));
      
      console.log('📄 File uploaded:', file.name);
      
      // Process PDF with OCR if it's a PDF file
      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        await processPDFWithOCR(file);
      }
    }
  };

  const processPDFWithOCR = async (file: File) => {
    if (!token) {
      console.error('❌ No authentication token available');
      return;
    }

    console.log('🚀 Starting OCR processing for file:', file.name);
    console.log('📄 File size:', file.size, 'bytes');
    console.log('📄 File type:', file.type);
    
    setIsProcessingOCR(true);
    
    try {
      console.log('🔗 OCR Endpoint URL:', API_ENDPOINTS.OCR_UPLOAD_AND_PROCESS);
      console.log('🔑 Token exists:', !!localStorage.getItem('token'));
      console.log('🌐 Environment:', process.env.REACT_APP_ENV || 'development');
      console.log('🔗 API Base URL:', process.env.REACT_APP_API_URL || 'https://rfq-backend.onrender.com');
      
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('📋 FormData entries:');
      formData.forEach((value, key) => {
        console.log(`  ${key}:`, value instanceof File ? `${value.name} (${value.size} bytes)` : value);
      });
      
      console.log('📤 Sending file to OCR endpoint...');
      const response = await fetch(API_ENDPOINTS.OCR_UPLOAD_AND_PROCESS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      console.log('📥 OCR response status:', response.status);
      console.log('📥 OCR response ok:', response.ok);
      
      if (response.ok) {
        const result = await response.json();
        console.log('📄 Full OCR result:', result);
        
        if (result.success && result.extracted_data) {
          const extractedInfo = result.extracted_data;
          console.log('✅ OCR processing successful!');
          console.log('📊 Extracted data:', extractedInfo);
          
          // Check if we have multiple products
          if (extractedInfo.products && extractedInfo.products.length > 1) {
            console.log('📦 Multiple products found:', extractedInfo.products.length);
            
            // Add currency information to each product
            const productsWithCurrency = extractedInfo.products.map((product: any) => ({
              ...product,
              currency: extractedInfo.document_info?.currency || 'EUR'
            }));
            
            setOcrProducts(productsWithCurrency);
            setOcrDocumentInfo(extractedInfo.document_info || {});
            setShowProductSelection(true);
          } else if (extractedInfo.products && extractedInfo.products.length === 1) {
            console.log('📦 Single product found, auto-filling...');
            const product = extractedInfo.products[0];
            const documentInfo = extractedInfo.document_info || {};
            
            // Add currency information to the product
            const productWithCurrency = {
              ...product,
              currency: documentInfo.currency || 'EUR'
            };
            
            // Auto-fill with the single product
            setFormData(prev => ({
              ...prev,
              ...productWithCurrency,
              ...documentInfo
            }));
            
            console.log('✅ Form data updated with single product');
          } else {
            console.log('📦 No products found, using fallback data');
            // Fallback to old format with OCR price information
            const fallbackData = {
              ...extractedInfo,
              // Include OCR price information if available
              unit_price: extractedInfo.unit_price || extractedInfo.price || 0,
              total_price: extractedInfo.total_price || extractedInfo.budget || 0,
              current_price: extractedInfo.current_price || extractedInfo.price || 0,
              // Mark as OCR generated
              is_ocr_generated: true
            };
            
            setFormData(prev => ({
              ...prev,
              ...fallbackData
            }));
          }
        } else {
          console.error('❌ OCR processing failed:', result.error);
          console.error('❌ Full error result:', result);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ OCR request failed:', response.status, response.statusText);
        console.error('❌ Error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error processing PDF with OCR:', error);
    } finally {
      console.log('🏁 OCR processing finished');
      setIsProcessingOCR(false);
    }
  };

  const handleProductSelect = (selectedProduct: any) => {
    console.log('✅ Product selected:', selectedProduct);
    
    // Extract price information from OCR data
    const ocrPriceInfo = {
      unit_price: selectedProduct.unit_price || selectedProduct.price || 0,
      total_price: selectedProduct.total_price || selectedProduct.budget || 0,
      current_price: selectedProduct.current_price || selectedProduct.price || 0,
      price: selectedProduct.price || 0,
      budget: selectedProduct.budget || selectedProduct.total_price || 0,
      currency: selectedProduct.currency || 'EUR'
    };
    
    // Store OCR data in form state for later submission
    const ocrData = {
      products: ocrProducts,
      document_info: ocrDocumentInfo
    };
    
    const ocrPrices = {
      unit_price: ocrPriceInfo.unit_price,
      total_price: ocrPriceInfo.total_price,
      currency: selectedProduct.currency || 'EUR',
      budget: ocrPriceInfo.budget
    };
    
    const ocrSupplierInfo = {
      supplier_name: ocrDocumentInfo.current_supplier || selectedProduct.current_supplier || '',
      supplier_email: ocrDocumentInfo.supplier_email || '',
      supplier_phone: ocrDocumentInfo.supplier_phone || '',
      supplier_address: ocrDocumentInfo.supplier_address || '',
      supplier_website: ocrDocumentInfo.supplier_website || ''
    };
    
    // Normalize currency from OCR (TL -> TRY)
    const normalizeCurrency = (currency: string) => {
      if (!currency) return getDefaultValues().currency; // default
      const upperCurrency = currency.toUpperCase();
      if (upperCurrency === 'TL' || upperCurrency === 'TURKISH LIRA') {
        return 'TRY';
      }
      return upperCurrency;
    };

    // Normalize currencies in OCR data
    const normalizedOcrDocumentInfo = {
      ...ocrDocumentInfo,
      currency: normalizeCurrency(ocrDocumentInfo.currency)
    };

    const normalizedOcrPriceInfo = {
      ...ocrPriceInfo,
      currency: normalizeCurrency(ocrPriceInfo.currency || selectedProduct.currency || 'EUR')
    };

    const normalizedOcrPrices = {
      ...ocrPrices,
      currency: normalizeCurrency(ocrPrices.currency)
    };

    console.log('🔍 OCR Document Info:', ocrDocumentInfo);
    console.log('🔍 OCR Supplier Info:', ocrSupplierInfo);
    console.log('🔍 Selected Product:', selectedProduct);
    console.log('🔍 OCR Price Info:', ocrPriceInfo);
    console.log('🔍 Full OCR Data:', ocrData);
    console.log('🔍 Normalized Currency - Document:', normalizedOcrDocumentInfo.currency);
    console.log('🔍 Normalized Currency - Price:', normalizedOcrPriceInfo.currency);
    
    // Combine product data with document info and price information
    setFormData(prev => {
      const newFormData = {
        ...prev,
        ...selectedProduct,
        ...normalizedOcrDocumentInfo,
        ...normalizedOcrPriceInfo,
        // Auto-fill supplier information from OCR
        current_supplier: normalizedOcrDocumentInfo.current_supplier || selectedProduct.current_supplier || prev.current_supplier,
        // Mark as OCR generated and store OCR data
        is_ocr_generated: true,
        ocr_data: ocrData,
        ocr_prices: normalizedOcrPrices,
        ocr_supplier_info: ocrSupplierInfo
      };
      
      console.log('🔍 Previous form data:', prev);
      console.log('🔍 New form data after OCR auto-fill:', newFormData);
      
      return newFormData;
    });
    
    setShowProductSelection(false);
    setOcrProducts([]);
    setOcrDocumentInfo({});
    
    console.log('✅ Form data updated with selected product and OCR data');
    console.log('✅ OCR Generated flag set to true');
    console.log('✅ OCR Data stored in form:', ocrData);
  };

  const handleCloseProductSelection = () => {
    setShowProductSelection(false);
    setOcrProducts([]);
    setOcrDocumentInfo({});
  };



  const extractInfoFromOCRText = (text: string): Partial<NewRequestFormData> => {
    const extracted: Partial<NewRequestFormData> = {
      // Product Information
      product_name: '',
      product_brand: '',
      product_global_code: '',
      product_company_code: '',
      product_description: '',
      
      // Request Information
      quantity: 1,
      unit: 'piece',
      budget: 0,
        currency: getDefaultValues().currency,
        delivery_time: '',
        importance: 'price' as const,
        region: getDefaultValues().region,
      category: 'IT',
      
      // Supplier Information
      current_supplier: '',
      favorite_suppliers: [],
      
      // Additional Specifications
      additional_specifications: [],
      custom_specifications: [],
      
      // OCR Price Information
      unit_price: 0,
      total_price: 0,
      
      // Legacy fields
      item: '',
      price: 0,
      firmName: '',
      urgency: 'normal' as const,
      currentPrice: 0,
      additionalNotes: '',
      customFilters: []
    };

    // Extract product information
    const productMatch = text.match(/product[:\s]+([^\n\r,]+)/i);
    if (productMatch) {
      extracted.product_name = productMatch[1].trim();
      extracted.item = productMatch[1].trim();
    }

    // Extract brand information
    const brandMatch = text.match(/brand[:\s]+([^\n\r,]+)/i);
    if (brandMatch) {
      extracted.product_brand = brandMatch[1].trim();
    }

    // Extract quantity
    const quantityMatch = text.match(/quantity[:\s]+(\d+)/i);
    if (quantityMatch) {
      extracted.quantity = parseInt(quantityMatch[1]);
    }

    // Extract unit
    const unitMatch = text.match(/unit[:\s]+([^\n\r,]+)/i);
    if (unitMatch) {
      extracted.unit = unitMatch[1].trim().toLowerCase();
    }

    // Extract price information
    const priceMatch = text.match(/price[:\s]*([\d,]+\.?\d*)/i);
    if (priceMatch) {
      const price = parseFloat(priceMatch[1].replace(/,/g, ''));
      extracted.current_price = price;
      extracted.price = price;
      extracted.unit_price = price;
    }

    // Extract total price
    const totalPriceMatch = text.match(/total[:\s]*([\d,]+\.?\d*)/i);
    if (totalPriceMatch) {
      const totalPrice = parseFloat(totalPriceMatch[1].replace(/,/g, ''));
      extracted.total_price = totalPrice;
      extracted.budget = totalPrice;
    }

    // Extract currency
    const currencyMatch = text.match(/currency[:\s]+([A-Z]{3})/i);
    if (currencyMatch) {
      extracted.currency = currencyMatch[1].toUpperCase();
    }

    // Extract supplier information
    const supplierMatch = text.match(/supplier[:\s]+([^\n\r,]+)/i);
    if (supplierMatch) {
      extracted.current_supplier = supplierMatch[1].trim();
      extracted.firmName = supplierMatch[1].trim();
    }

    // Extract delivery time
    const deliveryMatch = text.match(/delivery[:\s]+([^\n\r,]+)/i);
    if (deliveryMatch) {
      extracted.delivery_time = deliveryMatch[1].trim();
    }

    // Extract description
    const descriptionMatch = text.match(/description[:\s]+([^\n\r]+)/i);
    if (descriptionMatch) {
      extracted.product_description = descriptionMatch[1].trim();
      extracted.additionalNotes = descriptionMatch[1].trim();
    }

    return extracted;
  };

  const addFilterSuggestion = (suggestion: any) => {
    setNewCustomSpecKey(suggestion.key);
    setActiveFilterType(suggestion.type);
  };

  const addCustomSpecification = () => {
    if (newCustomSpecKey.trim() && newCustomSpecValue.trim()) {
      const newSpec = {
        key: newCustomSpecKey.trim(),
        value: newCustomSpecValue.trim()
      };
      
      setFormData(prev => ({
        ...prev,
        custom_specifications: [...prev.custom_specifications, newSpec]
      }));
      
      setNewCustomSpecKey('');
      setNewCustomSpecValue('');
    }
  };

  const removeCustomSpecification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      custom_specifications: prev.custom_specifications.filter((_, i) => i !== index)
    }));
  };

  const removeAdditionalSpecification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      additional_specifications: prev.additional_specifications.filter((_, i) => i !== index)
    }));
  };

  const addFavoriteSupplier = () => {
    if (newFavoriteSupplier.trim()) {
      setFormData(prev => ({
        ...prev,
        favorite_suppliers: [...(prev.favorite_suppliers || []), newFavoriteSupplier.trim()]
      }));
      setNewFavoriteSupplier('');
    }
  };

  const removeFavoriteSupplier = (index: number) => {
    setFormData(prev => ({
      ...prev,
      favorite_suppliers: (prev.favorite_suppliers || []).filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all required fields
    const errors: {[key: string]: string} = {};
    
    const productNameError = validateField('product_name', formData.product_name);
    if (productNameError) errors.product_name = productNameError;
    
    const quantityError = validateField('quantity', formData.quantity);
    if (quantityError) errors.quantity = quantityError;
    
    const budgetError = validateField('budget', formData.budget);
    if (budgetError) errors.budget = budgetError;
    
    // If there are validation errors, show them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Clear any existing validation errors
    setValidationErrors({});
    
    if (!token) {
      console.error('No authentication token available');
      return;
    }

    // Create request data object
    const requestData = {
      ...formData,
      // Ensure all required fields are present
      product_name: formData.product_name,
      product_brand: formData.product_brand || '',
      product_global_code: formData.product_global_code || '',
      product_company_code: formData.product_company_code || '', // Legacy field
      company_product_code: formData.company_product_code || '', // New dynamic field
      product_description: formData.product_description || '',
      
      // New Product Specifications
      dimensions_text: formData.dimensions_text || '',
      base_unit: formData.base_unit || { unit: 'adet', conversion_factor: 1.0 },
      
      quantity: formData.quantity,
      unit: formData.unit || 'piece',
      budget: formData.budget,
        currency: formData.currency || getDefaultValues().currency,
        delivery_time: formData.delivery_time || '',
        importance: formData.importance || 'price',
        region: formData.region || getDefaultValues().region,
      category: formData.category || 'IT',
      address: formData.address || '',
      current_supplier: formData.current_supplier || '',
      favorite_suppliers: formData.favorite_suppliers || [],
      selected_approved_suppliers: formData.selected_approved_suppliers || [],
      additional_specifications: formData.additional_specifications || [],
      custom_specifications: formData.custom_specifications || [],
      unit_price: formData.unit_price || 0,
      total_price: formData.total_price || 0,
      urgency: formData.urgency || 'normal',
      current_price: formData.current_price || 0,
      additional_notes: formData.additionalNotes || '',
      custom_filters: formData.customFilters || {},
      // OCR Information
      is_ocr_generated: formData.is_ocr_generated || false,
      ocr_data: formData.ocr_data || {},
      ocr_prices: formData.ocr_prices || {},
      ocr_supplier_info: formData.ocr_supplier_info || {}
    };

    console.log('📤 Submitting request with OCR data:', requestData);
    console.log('🔍 OCR Generated:', requestData.is_ocr_generated);
    console.log('🔍 OCR Data:', requestData.ocr_data);
    console.log('🔍 OCR Prices:', requestData.ocr_prices);
    console.log('🔍 OCR Supplier Info:', requestData.ocr_supplier_info);
    console.log('🔍 Additional Specifications:', requestData.additional_specifications);
    console.log('🔍 Custom Specifications:', requestData.custom_specifications);
    console.log('🔍 Form Data Keys:', Object.keys(formData));
    console.log('🔍 Form Data Product Name:', formData.product_name);
    console.log('🔍 Form Data Address:', formData.address);
    console.log('🔍 Form Data Selected Approved Suppliers:', formData.selected_approved_suppliers);
    console.log('🔍 Request Data Keys:', Object.keys(requestData));
    console.log('🔍 Request Data Product Name:', requestData.product_name);
    console.log('🔍 Request Data Address:', requestData.address);
    console.log('🔍 Request Data Selected Approved Suppliers:', requestData.selected_approved_suppliers);

    try {
      // Pass the data to parent component instead of making API call
      onSubmit(requestData);
      handleClose();
    } catch (error) {
      console.error('❌ Error submitting request:', error);
      alert('Error submitting request: ' + error);
    }
  };

  const handleClose = () => {
    setFormData({
      // Product Information
      product_name: '',
      product_brand: '',
      product_global_code: '',
      product_company_code: '',
      product_description: '',
      // Request Information
      quantity: '',
      unit: 'piece',
      budget: '',
        currency: getDefaultValues().currency,
        delivery_time: '',
        importance: 'price',
        region: getDefaultValues().region,
      category: 'IT', // Default category
      address: '', // Delivery address
      // Supplier Information
      current_supplier: '',
      favorite_suppliers: [],
      // Additional Specifications
      additional_specifications: [],
      custom_specifications: [],
      // OCR Price Information
      unit_price: 0,
      total_price: 0,
      // Legacy fields
      item: '',
      price: 0,
      firmName: '',
      urgency: 'normal',
      currentPrice: 0,
      additionalNotes: '',
      customFilters: [],
      file: undefined
    });
    setSelectedFile(null);
    setNewCustomSpecKey('');
    setNewCustomSpecValue('');
    setNewFavoriteSupplier('');
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editingRequest ? 
                (editingRequest.isReorder ? t('common.reorder_request') : 
                 (editingRequest.status === 'revision_required' ? t('common.revise_procurement_request') : t('common.edit_request'))) : 
                t('common.new_procurement_request')}</h2>
            <button className="close-button" onClick={handleClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="modal-form">
            {/* Revision Information - Show only when editing a revision (not for re-order) */}
            {editingRequest && editingRequest.revision_notes && !editingRequest.isReorder && (
              <div className="form-section revision-section">
                <h3 className="section-title revision-title">{t('common.revision_required')}</h3>
                <div className="revision-info">
                  <div className="revision-notes">
                    <label className="form-label">{t('common.revision_notes')}</label>
                    <div className="revision-notes-content">
                      {editingRequest.revision_notes}
                    </div>
                  </div>
                  {editingRequest.revision_requested_by && (
                    <div className="revision-meta">
                      <small>{t('common.requested_by')} {editingRequest.revision_requested_by}</small>
                      {editingRequest.revision_requested_at && (
                        <small>{t('common.date')} {new Date(editingRequest.revision_requested_at).toLocaleDateString()}</small>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* File Upload */}
            <div className="form-section">
              <h3 className="section-title">{t('common.document_upload')}</h3>
              <div className="form-group">
                <label className="form-label">{t('common.upload_file')}</label>
                <div className="file-upload">
                  <input
                    type="file"
                    id="file-upload"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.xls,.xlsx"
                    className="file-input"
                    disabled={isProcessingOCR}
                  />
                  <div className="file-upload-container">
                    <label htmlFor="file-upload" className="file-upload-label">
                      <div className="file-upload-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 15V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V15M17 8L12 3M12 3L7 8M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div className="file-upload-text">
                        <span className="file-upload-title">
                          {isProcessingOCR ? t('common.processing_pdf_ocr') : 
                           selectedFile ? selectedFile.name : t('common.choose_file_drag')}
                        </span>
                        <span className="file-upload-subtitle">
                          {t('common.drag_drop_or_click')}
                        </span>
                      </div>
                    </label>
                    <button 
                      type="button"
                      onClick={() => document.getElementById('file-upload')?.click()}
                      className="file-browse-button"
                      disabled={isProcessingOCR}
                    >
                      {t('common.browse_files')}
                    </button>
                  </div>
                </div>
                {isProcessingOCR && (
                  <div className="ocr-processing">
                    <div className="spinner"></div>
                    <span>{t('common.extracting_information')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Product Information */}
            <div className="form-section">
              <h3 className="section-title">{t('common.product_information')}</h3>

              <div className="form-group">
                <label className="form-label">
                  {t('common.product_name')} <span className="required-indicator">*</span>
                  <InfoTooltip text={t('common.product_name_tooltip')} />
                </label>
                <input
                  type="text"
                  id="product_name"
                  name="product_name"
                  value={formData.product_name}
                  onChange={handleInputChange}
                  placeholder={t('common.product_name_placeholder')}
                  required
                  className={`form-input ${validationErrors.product_name ? 'error' : ''}`}
                />
                {validationErrors.product_name && (
                  <div className="validation-error">{validationErrors.product_name}</div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    {t('common.product_brand')}
                    <InfoTooltip text={t('common.product_brand_tooltip')} />
                  </label>
                  <input
                    type="text"
                    name="product_brand"
                    value={formData.product_brand || ''}
                    onChange={handleInputChange}
                    placeholder={t('common.enter_brand_name')}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t('common.global_product_code')}
                    <InfoTooltip text={t('common.global_product_code_tooltip')} />
                  </label>
                  <input
                    type="text"
                    name="product_global_code"
                    value={formData.product_global_code || ''}
                    onChange={handleInputChange}
                    placeholder={t('common.enter_global_code')}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  {getCompanyProductCodeLabel()}
                  <InfoTooltip text={`Enter your company's product code`} />
                </label>
                <input
                  type="text"
                  name="company_product_code"
                  value={formData.company_product_code || ''}
                  onChange={handleInputChange}
                  placeholder={`Enter ${user?.company?.name || 'Company'} product code`}
                  className="form-input"
                />
              </div>
            </div>

            {/* Product Specifications */}
            <div className="form-section">
              <h3 className="section-title">{t('common.product_specifications')}</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    {t('common.dimensions')}
                    <InfoTooltip text={t('common.dimensions_tooltip')} />
                  </label>
                  <input
                    type="text"
                    name="dimensions_text"
                    value={formData.dimensions_text || ''}
                    onChange={handleInputChange}
                    placeholder={t('common.dimensions_placeholder')}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    {t('common.base_unit')}
                    <InfoTooltip text={t('common.base_unit_tooltip')} />
                  </label>
                  <div className="base-unit-inputs">
                    <select
                      name="base_unit.unit"
                      value={formData.base_unit?.unit || 'adet'}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="adet">{t('common.piece')}</option>
                      <option value="kg">{t('common.kilogram')}</option>
                      <option value="gr">{t('common.gram')}</option>
                      <option value="metre">{t('common.meter')}</option>
                      <option value="lt">{t('common.liter')}</option>
                      <option value="m2">{t('common.square_meter')}</option>
                      <option value="m3">{t('common.cubic_meter')}</option>
                    </select>
                    <input
                      type="number"
                      name="base_unit.conversion_factor"
                      value={formData.base_unit?.conversion_factor || 1.0}
                      onChange={handleInputChange}
                      placeholder={t('common.conversion_factor')}
                      className="form-input"
                      step="0.01"
                      min="0.01"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Request Information */}
            <div className="form-section">
              <h3 className="section-title">{t('common.request_information')}</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('common.quantity')} <span className="required-indicator">*</span></label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity || ''}
                    onChange={handleInputChange}
                    placeholder={t('common.enter_quantity')}
                    min="1"
                    required
                    className={`form-input ${validationErrors.quantity ? 'error' : ''}`}
                  />
                  {validationErrors.quantity && (
                    <div className="validation-error">{validationErrors.quantity}</div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('common.unit')}</label>
                  <select
                    name="unit"
                    value={formData.unit || 'piece'}
                    onChange={handleInputChange}
                    required
                    className="form-select"
                  >
                    {UNIT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('common.budget')} <span className="required-indicator">*</span></label>
                  <input
                    type="number"
                    name="budget"
                    value={formData.budget || ''}
                    onChange={handleInputChange}
                    placeholder={t('common.enter_budget')}
                    min="0"
                    step="0.01"
                    required
                    className={`form-input ${validationErrors.budget ? 'error' : ''}`}
                  />
                  {validationErrors.budget && (
                    <div className="validation-error">{validationErrors.budget}</div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">{t('common.currency')}</label>
                  <select
                    name="currency"
                    value={formData.currency || getDefaultValues().currency}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    {CURRENCY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('common.delivery_time')}</label>
                  <DatePicker
                    value={formData.delivery_time || ''}
                    onChange={(date) => {
                      setFormData(prev => ({
                        ...prev,
                        delivery_time: date
                      }));
                    }}
                    placeholder={t('common.delivery_time_placeholder')}
                    minDate={new Date().toISOString().split('T')[0]} // Bugünden başla
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('common.importance')}</label>
                  <select
                    name="importance"
                    value={formData.importance || 'price'}
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    <option value="time">{t('common.time_critical')}</option>
                    <option value="price">{t('common.price_critical')}</option>
                    <option value="quality">{t('common.quality_critical')}</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('common.delivery_address')}</label>
                <div className="address-input-container">
                  <input
                    type="text"
                    name="address"
                    value={formData.address || ''}
                    onChange={handleInputChange}
                    placeholder={t('common.enter_delivery_address')}
                    className="form-input"
                  />
                  <button
                    type="button"
                    onClick={() => setIsAddressModalOpen(true)}
                    className="address-select-btn"
                    title={t('common.select_saved_address')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('common.category')}</label>
                <select
                  name="category"
                  value={formData.category || 'IT'}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                >
                  {CATEGORY_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">{t('common.region')}</label>
                <div className="region-checkboxes">
                  {REGION_OPTIONS.map(option => (
                    <label key={option.value} className="region-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.region.includes(option.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              region: [...prev.region, option.value]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              region: prev.region.filter(r => r !== option.value)
                            }));
                          }
                        }}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Supplier Information */}
            <div className="form-section">
              <h3 className="section-title">{t('common.supplier_information')}</h3>
              
              <div className="form-group">
                <label className="form-label">{t('common.current_supplier')}</label>
                <input
                  type="text"
                  name="current_supplier"
                  value={formData.current_supplier || ''}
                  onChange={handleInputChange}
                  placeholder={t('common.enter_current_supplier')}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('common.add_favorite_suppliers')}</label>
                <div className="add-supplier-row">
                  <input
                    type="text"
                    value={newFavoriteSupplier}
                    onChange={(e) => setNewFavoriteSupplier(e.target.value)}
                    placeholder={t('common.enter_supplier_name')}
                    className="form-input"
                  />
                  <button
                    type="button"
                    onClick={addFavoriteSupplier}
                    className="add-supplier-button"
                    disabled={!newFavoriteSupplier.trim()}
                  >
                    {t('common.add')}
                  </button>
                </div>
                
                {/* Add Approved Suppliers Button */}
                <div className="approved-suppliers-section">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('🔍 Add Approved Suppliers button clicked');
                      console.log('🔍 user object:', user);
                      console.log('🔍 userCompanyId:', user?.company_id);
                      setIsApprovedSuppliersModalOpen(true);
                    }}
                    className="add-approved-suppliers-button"
                  >
                    + {t('common.add_approved_suppliers')}
                  </button>
                </div>
                
                {formData.favorite_suppliers && formData.favorite_suppliers.length > 0 && (
                  <div className="favorite-suppliers">
                    {formData.favorite_suppliers.map((supplier, index) => (
                      <div key={index} className="supplier-item">
                        <span>{supplier}</span>
                        <button
                          type="button"
                          onClick={() => removeFavoriteSupplier(index)}
                          className="remove-supplier-button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Display Selected Approved Suppliers */}
                {formData.selected_approved_suppliers && formData.selected_approved_suppliers.length > 0 && (
                  <div className="approved-suppliers-list">
                    <label className="form-label">{t('common.selected_approved_suppliers')}:</label>
                    <div className="suppliers-tags">
                      {formData.selected_approved_suppliers.map((supplierName, index) => (
                        <span key={index} className="supplier-tag approved">
                          {supplierName}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Specifications */}
            <div className="form-section">
              <h3 className="section-title">
                {t('common.additional_specifications')}
                <InfoTooltip text={t('common.additional_specifications_tooltip')} />
              </h3>
              
              <div className="form-group">
                <label className="form-label">{t('common.filter_suggestions')}</label>
                <div className="filter-suggestions">
                  {FILTER_SUGGESTIONS.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => addFilterSuggestion(suggestion)}
                      className="filter-suggestion-button"
                    >
                      {suggestion.label}
                    </button>
                  ))}
                </div>
                
                {/* Display existing additional specifications */}
                {formData.additional_specifications && formData.additional_specifications.length > 0 && (
                  <div className="existing-specifications">
                    <label className="form-label">{t('common.existing_specifications')}</label>
                    <div className="specifications-list">
                      {formData.additional_specifications.map((spec, index) => (
                        <div key={index} className="spec-item existing">
                          <span className="spec-text">{spec}</span>
                          <button
                            type="button"
                            onClick={() => removeAdditionalSpecification(index)}
                            className="remove-spec-button"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">{t('common.custom_specifications')}</label>
                <div className="add-spec-row">
                  <input
                    type="text"
                    value={newCustomSpecKey}
                    onChange={(e) => setNewCustomSpecKey(e.target.value)}
                    placeholder={t('common.specification_name_placeholder')}
                    className="form-input"
                  />
                  {activeFilterType === 'dropdown' ? (
                    <select
                      value={newCustomSpecValue}
                      onChange={(e) => setNewCustomSpecValue(e.target.value)}
                      className="form-select"
                    >
                      <option value="">{t('common.select_value')}</option>
                      {FILTER_SUGGESTIONS.find(s => s.key === newCustomSpecKey)?.options?.map((option: string, index: number) => (
                        <option key={index} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={newCustomSpecValue}
                      onChange={(e) => setNewCustomSpecValue(e.target.value)}
                      placeholder={FILTER_SUGGESTIONS.find(s => s.key === newCustomSpecKey)?.placeholder || t('common.enter_specification_value')}
                      className="form-input"
                    />
                  )}
                  <button
                    type="button"
                    onClick={addCustomSpecification}
                    className="add-spec-button"
                    disabled={!newCustomSpecKey.trim() || !newCustomSpecValue.trim()}
                  >
                    {t('common.add')}
                  </button>
                </div>

                {formData.custom_specifications.length > 0 && (
                  <div className="custom-specifications">
                    {formData.custom_specifications.map((spec, index) => (
                      <div key={index} className="spec-item">
                        <span className="spec-key">{spec.key}:</span>
                        <span className="spec-value">{spec.value}</span>
                        <button
                          type="button"
                          onClick={() => removeCustomSpecification(index)}
                          className="remove-spec-button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button type="button" onClick={handleClose} className="cancel-button" disabled={isSubmitting}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 
                  (editingRequest ? 
                    (editingRequest.isReorder ? t('common.creating') : t('common.updating')) : 
                    t('common.creating')) : 
                  (editingRequest ? 
                    (editingRequest.isReorder ? t('common.create_request') : t('common.update_request')) : 
                    t('common.create_request'))}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Product Selection Modal */}
      <ProductSelectionModal
        isOpen={showProductSelection}
        onClose={handleCloseProductSelection}
        products={ocrProducts}
        onProductSelect={handleProductSelect}
      />

      {/* Address Selection Modal */}
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onAddressSelected={handleAddressSelect}
        isSelectionMode={true}
      />

      {/* Approved Suppliers Modal */}
              <ApprovedSuppliersModal
          isOpen={isApprovedSuppliersModalOpen}
          onClose={() => setIsApprovedSuppliersModalOpen(false)}
          onSelectSuppliers={handleApprovedSuppliersSelect}
          selectedSuppliers={formData.selected_approved_suppliers || []}
          userCompanyId={user?.company_id}
        />
    </>
  );
};

export default NewRequestModal; 